import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import { useBreakpointFits } from 'use-react-breakpoint';

import { useUserSettings } from '../../hooks';

type AppContextType = {
  isMobile: boolean;

  logoType: LogoType | null;
  setLogoType: (logoType: LogoType) => void;

  menuOpened: boolean;
  toggleMenu: () => void;
};

const AppContext = createContext<AppContextType | undefined>(undefined);

type AppProviderProps = {
  children: React.ReactNode;
};

export const AppProvider: React.FC<AppProviderProps> = ({ children }) => {
  const isMobile = !useBreakpointFits('sm');
  const [logoType, setLogoTypeFunc] = useUserSettings<LogoType | null>(
    null,
    'logo'
  );
  const setLogoType = useCallback<typeof setLogoTypeFunc>(
    (...params) => setLogoTypeFunc(...params),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );
  const [menuOpened, setMenuOpened] = useState(false);
  const toggleMenu = useCallback(() => {
    setMenuOpened(o => !o);
  }, []);

  const value = useMemo(() => {
    return { isMobile, logoType, menuOpened, setLogoType, toggleMenu };
  }, [isMobile, logoType, menuOpened, setLogoType, toggleMenu]);

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
};

export const useAppContext = (): AppContextType => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useAppContext must be used within an AppProvider');
  }
  return context;
};
